import commonHi from "./hi-common.json";
import dynamicHi from "./hi-dynamic-templates.json";
import locationsHi from "./locations-hi.json";

export const SUPPORTED_LOCALES = Object.freeze(["en", "hi"]);
export const PREFERRED_LOCALE_KEY = "jstos.preferredLocale";

const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const templatesHi = Object.freeze(Object.fromEntries(
  dynamicHi
    .filter((entry) => typeof entry.english_template === "string" && typeof entry.hindi_template === "string")
    .map((entry) => [entry.english_template, entry.hindi_template])
));

export function normalizeLocale(locale) {
  return locale === "hi" ? "hi" : "en";
}

const splitPath = (path) => path.match(/^([^?#]*)(.*)$/).slice(1);
const isRootPath = (path) => typeof path === "string" && path.startsWith("/") && !path.startsWith("//") && !path.includes("\\");
const stripPrefix = (pathname) => pathname.replace(/^\/hi(?=\/|$)/, "") || "/";
const normalizePagePath = (pathname) => `${pathname.replace(/\/{2,}/g, "/").replace(/\/+$/, "")}/`;

// Paths for downloads, API requests and browser resources are never locale links.
function isPagePath(pathname) {
  const raw = stripPrefix(pathname);
  return !/\.[^/]+\/?$/.test(raw) && !/^\/(?:api|static|assets|images|img|fonts|locales|media|uploads|\.well-known)(?:\/|$)/i.test(raw);
}

/** The URL is authoritative. Reading a saved preference never redirects a URL. */
export function localeFromPath(path) {
  const pathname = typeof path === "object" && path ? path.pathname : path;
  return typeof pathname === "string" && /^\/hi(?:\/|[?#]|$)/.test(pathname) ? "hi" : "en";
}

/** Remove the locale prefix; preserve query/hash, protected links and Route To objects. */
export function basePath(path) {
  if (path && typeof path === "object") {
    return typeof path.pathname === "string" ? { ...path, pathname: basePath(path.pathname) } : path;
  }
  if (!isRootPath(path)) return path;
  const [pathname, suffix] = splitPath(path);
  if (!isPagePath(pathname)) return path;
  return `${normalizePagePath(stripPrefix(pathname))}${suffix}`;
}

/** Canonical, trailing-slash page URLs. External, tel/mailto, hash and asset URLs pass through. */
export function localizePath(path, locale = "en") {
  if (path && typeof path === "object") {
    return typeof path.pathname === "string" ? { ...path, pathname: localizePath(path.pathname, locale) } : path;
  }
  if (!isRootPath(path)) return path;
  const [pathname, suffix] = splitPath(path);
  if (!isPagePath(pathname)) return path;
  const raw = normalizePagePath(stripPrefix(pathname));
  return `${normalizeLocale(locale) === "hi" ? `/hi${raw}` : raw}${suffix}`;
}

function translatedTemplate(text, locale) {
  if (typeof text !== "string" || normalizeLocale(locale) !== "hi") return text;
  if (owns(commonHi, text)) return commonHi[text];
  return owns(templatesHi, text) ? templatesHi[text] : text;
}

/** Named substitution only. Values such as phone numbers, fares and backend enums are caller-owned. */
export function interpolate(template, values = {}) {
  if (typeof template !== "string") return template;
  return template.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (token, key) =>
    owns(values, key) && values[key] !== undefined && values[key] !== null ? String(values[key]) : token
  );
}

export function translate(text, locale = "en", values = {}) {
  return interpolate(translatedTemplate(text, locale), values);
}

/** For a sentence containing existing React Link/strong nodes; never parses or injects HTML. */
export function translateParts(text, locale = "en", values = {}) {
  const template = translatedTemplate(text, locale);
  if (typeof template !== "string") return template;
  const parts = [];
  const pattern = /\{([A-Za-z][A-Za-z0-9_]*)\}/g;
  let previousEnd = 0;
  let match;
  while ((match = pattern.exec(template))) {
    if (match.index > previousEnd) parts.push(template.slice(previousEnd, match.index));
    parts.push(owns(values, match[1]) && values[match[1]] !== undefined && values[match[1]] !== null ? values[match[1]] : match[0]);
    previousEnd = pattern.lastIndex;
  }
  if (previousEnd < template.length) parts.push(template.slice(previousEnd));
  return parts;
}

export function cityName(raw, locale = "en") {
  if (typeof raw !== "string" || normalizeLocale(locale) !== "hi") return raw;
  return owns(locationsHi, raw) ? locationsHi[raw].cityLabel || raw : translate(raw, locale);
}

const CONTENT_FIELDS = Object.freeze([
  "heroAlt", "title", "description", "h1", "intro", "intro2", "pickupInfo", "dispatch",
  "highlights", "serviceTypes", "faqs",
]);

/** Localize display content without replacing city, slug, routing, fleet or backend values. */
export function getLocalizedLocation(record, locale = "en") {
  if (!record || typeof record !== "object") return record;
  const isHindi = normalizeLocale(locale) === "hi";
  const content = isHindi && owns(locationsHi, record.city) ? locationsHi[record.city] : null;
  const localized = { ...record };
  if (content) {
    for (const field of CONTENT_FIELDS) {
      if (owns(content, field)) {
        localized[field] = Array.isArray(content[field])
          ? content[field].map((item) => item && typeof item === "object" ? { ...item } : item)
          : content[field];
      }
    }
    // Keep every original destination and other connection metadata. Match labels by raw destination.
    if (Array.isArray(record.connections) && Array.isArray(content.connections)) {
      localized.connections = record.connections.map((connection) => {
        const label = content.connections.find((entry) => entry.to === connection.to)?.label;
        return typeof label === "string" ? { ...connection, label } : { ...connection };
      });
    }
  }
  localized.cityLabel = isHindi ? cityName(record.city, locale) : record.cityLabel || record.city;
  localized.displayName = content?.displayName || (isHindi ? localized.cityLabel : record.displayName || localized.cityLabel);
  return localized;
}
